import { IoArrowBack, IoLockClosedOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { useContext, useEffect, useMemo, useState } from "react";
import { BiMinus, BiPlus } from "react-icons/bi";
import { TextField } from "@mui/material";
import { IoTrashOutline } from "react-icons/io5";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addToCart,
  checkAllCarts,
  deleteAllCarts,
  getListCart,
  getListCartInfo,
} from "../../../services/purchaseApi";
import { MdExpandLess, MdExpandMore } from "react-icons/md";
import { GlobalStateContext } from "../../../components/context/GlobalStateContext";
import SwipeConfirm from "../../../components/swipeComp/SwipeConfirm";
import NotFound from "../../../assets/notFound.png";
import { getListMyLocation } from "../../../services/profileApi";
import { useNotification } from "../../../components/context/NotificationContext";

const SkeletonLoader = () => {
  return (
    <div className="space-y-3 w-full">
      <div className="bg-white rounded-xl p-3 animate-pulse md:rounded-2xl md:border md:border-slate-200/80 md:shadow-sm">
        <div className="flex gap-2 items-start pb-2 border-b border-slate-200">
          <div className="w-4 h-4 bg-slate-200 rounded shrink-0 mt-1"></div>
          <div className="flex flex-col flex-1 gap-2">
            <div className="h-5 bg-slate-200 rounded-md w-48"></div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 bg-slate-200 rounded"></div>
              <div className="h-4 bg-slate-200 rounded-md w-40"></div>
            </div>
          </div>
          <div className="w-4 h-4 bg-slate-200 rounded shrink-0 mt-1"></div>
        </div>

        <div className="pt-2 space-y-2">
          {[1, 2].map((item) => (
            <div key={item}>
              <div className="flex gap-2">
                <div className="w-4 h-4 bg-slate-200 rounded shrink-0 mt-1"></div>
                <div className="flex flex-col items-start flex-1 min-w-0">
                  <div className="h-5 bg-slate-200 rounded-md w-full max-w-xs mb-2 mt-1"></div>
                  <div className="flex items-start gap-2 sm:gap-3 w-full">
                    <div className="w-20 h-20 bg-slate-200 rounded-xl shrink-0"></div>
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="h-4 bg-slate-200 rounded-md w-24"></div>
                      <div className="h-5 bg-slate-200 rounded-md w-32"></div>
                      <div className="flex items-center justify-between gap-2 w-full">
                        <div className="h-10 bg-slate-200 rounded-lg w-32"></div>
                        <div className="h-10 w-10 bg-slate-200 rounded-full shrink-0"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {item < 2 && <hr className="my-2" />}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl p-3 animate-pulse md:rounded-2xl md:border md:border-slate-200/80 md:shadow-sm">
        <div className="flex gap-2">
          <div className="w-4 h-4 bg-slate-200 rounded shrink-0 mt-1"></div>
          <div className="flex flex-col items-start flex-1 min-w-0">
            <div className="h-5 bg-slate-200 rounded-md w-full max-w-xs mb-2 mt-1"></div>
            <div className="flex items-start gap-2 sm:gap-3 w-full">
              <div className="w-20 h-20 bg-slate-200 rounded-xl shrink-0"></div>
              <div className="flex-1 min-w-0 space-y-2">
                <div className="h-4 bg-slate-200 rounded-md w-24"></div>
                <div className="h-5 bg-slate-200 rounded-md w-32"></div>
                <div className="flex items-center justify-between gap-2 w-full">
                  <div className="h-10 bg-slate-200 rounded-lg w-32"></div>
                  <div className="h-10 w-10 bg-slate-200 rounded-full shrink-0"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-3 animate-pulse md:rounded-2xl md:border md:border-slate-200/80 md:shadow-sm">
        <div className="flex gap-2">
          <div className="w-4 h-4 bg-slate-200 rounded shrink-0 mt-1"></div>
          <div className="flex flex-col items-start flex-1 min-w-0">
            <div className="h-5 bg-slate-200 rounded-md w-2/3 mb-2 mt-1"></div>
            <div className="flex items-start gap-2 sm:gap-3 w-full">
              <div className="w-20 h-20 bg-slate-200 rounded-xl shrink-0"></div>
              <div className="flex-1 min-w-0 space-y-2">
                <div className="h-4 bg-slate-200 rounded-md w-24"></div>
                <div className="h-5 bg-slate-200 rounded-md w-32"></div>
                <div className="flex items-center justify-between gap-2 w-full">
                  <div className="h-10 bg-slate-200 rounded-lg w-32"></div>
                  <div className="h-10 w-10 bg-slate-200 rounded-full shrink-0"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const formatRupiah = (number) => {
  return number ? Number(number).toLocaleString("id-ID") : 0;
};

const CartDetailNew = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const machineId = localStorage.getItem("MachineId");
  const companyId = localStorage.getItem("companyId");
  const { isLogin } = useContext(GlobalStateContext);
  const { showNotification } = useNotification();

  const [expandedItems, setExpandedItems] = useState({});
  const [dataBuffer, setDataBuffer] = useState([]);
  const [tempQuantities, setTempQuantities] = useState({});
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const formatImageToArray = (image) => {
    if (Array.isArray(image)) {
      return image;
    } else if (typeof image === "string") {
      return [image];
    }
    return [];
  };

  const groupCartDataByDoctor = (cartData) => {
    const grouped = {
      withDoctor: {},
      withoutDoctor: [],
    };

    if (!cartData || cartData.length === 0) return grouped;

    cartData.forEach((item) => {
      const hasDoctor =
        item.recipe_doctor_id !== 0 && item.recipe_doctor_id !== null;
      const consultationId = item.consultation_id || null;

      if (hasDoctor && consultationId) {
        if (!grouped.withDoctor[consultationId]) {
          grouped.withDoctor[consultationId] = {
            consultationId,
            doctorId: item.recipe_doctor_id,
            doctorName:
              item.recipe_doctor_name || `Dokter #${item.recipe_doctor_id}`,
            items: [],
            expanded: expandedItems[consultationId] || false,
          };
        }
        grouped.withDoctor[consultationId].items.push(item);
      } else if (hasDoctor && !consultationId) {
        const fallbackKey = `doctor-${item.recipe_doctor_id}`;
        if (!grouped.withDoctor[fallbackKey]) {
          grouped.withDoctor[fallbackKey] = {
            consultationId: null,
            doctorId: item.recipe_doctor_id,
            doctorName:
              item.recipe_doctor_name || `Dokter #${item.recipe_doctor_id}`,
            items: [],
            expanded: expandedItems[fallbackKey] || false,
          };
        }
        grouped.withDoctor[fallbackKey].items.push(item);
      } else {
        grouped.withoutDoctor.push(item);
      }
    });

    return grouped;
  };

  const toggleExpand = (key) => {
    setExpandedItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleAddToCart = (item, newQuantity, newCheckState = null) => {
    const formattedImage = formatImageToArray(item.image);

    mutateAddToCart({
      company_id: item.company_id,
      recipe_doctor_id: item.recipe_doctor_id,
      price: item.price,
      product_id: item.product_id,
      product_name: item.product_name,
      image: formattedImage,
      product_type: item.product_type,
      quantity: newQuantity,
      stock: item.stock,
      check_state: newCheckState !== null ? newCheckState : item.check_state,
      uuid: machineId,
    });

    setDataBuffer((prev) =>
      prev.map((itm) =>
        itm.id === item.id ? { ...itm, isLoading: true } : itm,
      ),
    );
  };

  const handleSuccessAddToCart = () => {
    setDataBuffer((prev) =>
      prev.map((item) => ({ ...item, isLoading: false })),
    );

    queryClient.invalidateQueries({
      queryKey: ["cart-detail"],
    });
    queryClient.invalidateQueries({
      queryKey: ["count-cart"],
    });
  };

  const { data: dataList } = useQuery({
    queryKey: ["list-my-location"],
    queryFn: () => getListMyLocation(),
    enabled: !!isLogin,
  });

  const {
    data: cartDetailData,
    isFetching: isCartLoading,
    refetch: refetchCart,
  } = useQuery({
    queryKey: ["cart-detail"],
    queryFn: () => getListCart(),
  });

  const { data: countCartData, refetch: refetchCountCart } = useQuery({
    queryKey: ["count-cart"],
    queryFn: () => getListCartInfo(),
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const { mutate: mutateAddToCart, isLoading: isAddToCartLoading } =
    useMutation({
      mutationFn: addToCart,
      onSuccess: () => handleSuccessAddToCart(),
      onError: () => {
        setDataBuffer((prev) =>
          prev.map((item) => ({ ...item, isLoading: false })),
        );
      },
    });

  const { mutate: mutateCheckCart } = useMutation({
    mutationFn: checkAllCarts,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["cart-detail"] });
      queryClient.invalidateQueries({ queryKey: ["count-cart"] });
      refetchCountCart();
      refetchCart();
    },
  });

  const { mutate: mutateDeleteAllCart } = useMutation({
    mutationFn: deleteAllCarts,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["cart-detail"] });
      queryClient.invalidateQueries({ queryKey: ["count-cart"] });
      refetchCountCart();
      refetchCart();
    },
  });

  const handleRegularCheckboxChange = (item) => {
    const newCheckState = !item.check_state;

    const body = {
      cart_ids: [item.id],
      check: newCheckState,
      uuid: machineId,
      company_id: Number(companyId),
      recipe_doctor_id: 0,
    };

    mutateCheckCart(body);
  };

  const handleDoctorCheckboxChange = (item) => {
    const newCheckState = !item.check_state;

    const body = {
      cart_ids: [item.id],
      check: newCheckState,
      uuid: machineId,
      company_id: Number(companyId),
      consultation_id: item.consultation_id,
    };

    mutateCheckCart(body);
  };

  const handleDoctorCheckAllChange = (doctorGroup, e) => {
    const newCheckState = e.target.checked;
    const allItemIds = doctorGroup.items.map((item) => item.id);

    const body = {
      cart_ids: allItemIds,
      check: newCheckState,
      uuid: machineId,
      company_id: Number(companyId),
      consultation_id: doctorGroup.consultationId,
    };

    mutateCheckCart(body);
  };

  const handleCheckboxChange = (
    item,
    isDoctorPrescription = false,
    doctorGroup = null,
  ) => {
    if (isDoctorPrescription && doctorGroup) {
      handleDoctorCheckboxChange(item, doctorGroup);
    } else {
      handleRegularCheckboxChange(item);
    }
  };

  const handleMinusClick = (item, isDoctorPrescription = false) => {
    if (item.isLoading || !item.check_state || isDoctorPrescription) return;

    const newQuantity = item.quantity - 1;

    if (newQuantity === 0) {
      handleAddToCart(item, 0, false);
    } else {
      handleAddToCart(item, newQuantity);
    }
  };

  const handlePlusClick = (item, isDoctorPrescription = false) => {
    if (
      item.isLoading ||
      !item.check_state ||
      item.quantity >= item.stock ||
      isDoctorPrescription
    )
      return;

    const newQuantity = item.quantity + 1;
    handleAddToCart(item, newQuantity);
  };

  const handleInputChange = (item, value, isDoctorPrescription = false) => {
    if (isDoctorPrescription) return;

    const numericValue = value.replace(/\D/g, "");

    if (numericValue === "") {
      setTempQuantities((prev) => ({
        ...prev,
        [item.id]: "",
      }));
      return;
    }

    let newQuantity = parseInt(numericValue, 10);

    newQuantity = Math.max(1, newQuantity);

    newQuantity = Math.min(newQuantity, item.stock);

    setTempQuantities((prev) => ({
      ...prev,
      [item.id]: newQuantity,
    }));
  };

  const handleInputBlur = (item, isDoctorPrescription = false) => {
    if (isDoctorPrescription) return;

    const tempQuantity = tempQuantities[item.id];

    if (
      tempQuantity !== undefined &&
      tempQuantity !== "" &&
      tempQuantity !== item.quantity
    ) {
      if (tempQuantity === 0) {
        handleAddToCart(item, 0, false);
      } else {
        handleAddToCart(item, tempQuantity);
      }
    }

    setTempQuantities((prev) => {
      const newState = { ...prev };
      delete newState[item.id];
      return newState;
    });
  };

  const handleInputKeyPress = (item, e, isDoctorPrescription = false) => {
    if (isDoctorPrescription) return;

    if (e.key === "Enter") {
      e.target.blur();
    }
  };

  const handleDeleteClick = (item) => {
    const body = {
      cart_ids: [item.id],
      uuid: machineId,
    };

    mutateDeleteAllCart(body);
  };

  const getFirstImage = (imageArray) => {
    if (Array.isArray(imageArray) && imageArray.length > 0) {
      return imageArray[0];
    }
    return "";
  };

  const renderRegularProductItem = (item, index, array) => {
    const displayQuantity =
      tempQuantities[item.id] !== undefined
        ? tempQuantities[item.id]
        : item.quantity;

    return (
      <div key={`${item.id}-${index}`}>
        <div className="flex gap-2">
          <input
            type="checkbox"
            checked={item.check_state}
            disabled={item.isLoading}
            onChange={() => handleCheckboxChange(item, false)}
            className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary focus:ring-2 shrink-0 mt-1 lg:mt-2 disabled:opacity-50"
          />
          <div className="flex flex-col items-start flex-1 min-w-0">
            <p className="text-md lg:text-lg font-medium mt-1 mb-2">
              {item.product_name}
            </p>
            <div className="text-gray-500 flex items-start gap-2 sm:gap-3 md:gap-4 w-full">
              <div className="w-20 h-20 md:w-24 md:h-24 object-cover rounded-xl overflow-hidden border border-gray-200 shrink-0 mt-0.5">
                <img
                  src={getFirstImage(item.image)}
                  alt={item.product_name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm lg:text-base">
                  Stok: {item.stock} {item.product_type}
                </p>
                <p className="text-base lg:text-xl font-semibold mb-1 text-slate-700 tabular-nums">
                  Rp{formatRupiah(item.price)}
                </p>
                <div className="flex items-center justify-between gap-2 w-full">
                  <div className="flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-200 text-slate-600 bg-white">
                    <button
                      onClick={() => handleMinusClick(item, false)}
                      disabled={
                        item.isLoading ||
                        !item.check_state ||
                        item.quantity <= 1
                      }
                      className="p-1 hover:bg-error hover:text-white rounded-full transition-all duration-200 ease-in-out select-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <BiMinus size={16} />
                    </button>

                    <TextField
                      value={displayQuantity}
                      onChange={(e) =>
                        handleInputChange(item, e.target.value, false)
                      }
                      onBlur={() => handleInputBlur(item, false)}
                      onKeyPress={(e) => handleInputKeyPress(item, e, false)}
                      disabled={item.isLoading}
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        className:
                          "text-center w-8 sm:w-16 [&_input]:text-center [&_input]:p-0 [&_input]:text-sm [&_input]:font-medium disabled:opacity-50",
                      }}
                      inputProps={{
                        style: { textAlign: "center" },
                        className: "text-center",
                        min: 1,
                        max: item.stock,
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "24px",
                          minHeight: "24px",
                          fontFamily: '"Montserrat", sans-serif',
                          "& input": {
                            textAlign: "center",
                            padding: "0",
                            fontSize: "14px",
                            height: "24px",
                            fontWeight: "500",
                            fontFamily: '"Montserrat", sans-serif',
                          },
                          "&.Mui-disabled": {
                            opacity: 0.5,
                          },
                        },
                      }}
                    />

                    <button
                      onClick={() => handlePlusClick(item, false)}
                      disabled={
                        item.isLoading ||
                        !item.check_state ||
                        item.quantity >= item.stock
                      }
                      className="p-1 hover:bg-green-500 hover:text-white rounded-full transition-all duration-200 ease-in-out select-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <BiPlus size={16} />
                    </button>
                  </div>

                  <button
                    onClick={() => {
                      setSelectedItem(item);
                      setOpenModalDelete(true);
                    }}
                    disabled={item.isLoading}
                    className="h-10 w-10 flex items-center justify-center text-error hover:text-error hover:bg-red-200 rounded-full transition-colors cursor-pointer shrink-0 disabled:opacity-50"
                  >
                    <IoTrashOutline size={22} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {index < array.length - 1 && <hr className="my-2" />}
      </div>
    );
  };

  const renderDoctorPrescriptionItem = (item, index, array, doctorGroup) => {
    const displayQuantity =
      tempQuantities[item.id] !== undefined
        ? tempQuantities[item.id]
        : item.quantity;

    return (
      <div key={`${item.id}-${index}`}>
        <div className="flex gap-2">
          <input
            type="checkbox"
            checked={item.check_state}
            disabled={item.isLoading}
            onChange={() => handleCheckboxChange(item, true, doctorGroup)}
            className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary focus:ring-2 shrink-0 mt-1 lg:mt-2 disabled:opacity-50"
          />
          <div className="flex flex-col items-start flex-1 min-w-0">
            <p className="text-md lg:text-lg font-medium mt-1 mb-2">
              {item.product_name}
            </p>
            <div className="text-gray-500 flex items-start gap-2 sm:gap-3 md:gap-4 w-full">
              <div className="w-20 h-20 md:w-24 md:h-24 object-cover rounded-xl overflow-hidden border border-gray-200 shrink-0 mt-0.5">
                <img
                  src={getFirstImage(item.image)}
                  alt={item.product_name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm lg:text-base">
                  Stok: {item.stock} {item.product_type}
                </p>
                <p className="text-base lg:text-xl font-semibold mb-1 text-slate-700 tabular-nums">
                  Rp{formatRupiah(item.price)}
                </p>
                <div className="flex items-center justify-between gap-2 w-full">
                  <div className="flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-300 text-slate-600 bg-gray-100">
                    <button
                      disabled={true}
                      className="p-1 text-slate-400 rounded-full cursor-not-allowed"
                    >
                      <BiMinus size={16} />
                    </button>

                    <TextField
                      value={displayQuantity}
                      disabled={true}
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        className:
                          "text-center w-8 sm:w-16 [&_input]:text-center [&_input]:p-0 [&_input]:text-sm [&_input]:font-medium",
                        readOnly: true,
                      }}
                      inputProps={{
                        style: { textAlign: "center" },
                        className: "text-center text-slate-500",
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "24px",
                          minHeight: "24px",
                          fontFamily: '"Montserrat", sans-serif',
                          "& input": {
                            textAlign: "center",
                            padding: "0",
                            fontSize: "14px",
                            height: "24px",
                            fontWeight: "500",
                            fontFamily: '"Montserrat", sans-serif',
                            color: "#64748b",
                          },
                          "&.Mui-disabled": {
                            color: "#64748b",
                            WebkitTextFillColor: "#64748b",
                          },
                        },
                      }}
                    />

                    <button
                      disabled={true}
                      className="p-1 text-slate-400 rounded-full cursor-not-allowed"
                    >
                      <BiPlus size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-1 text-xs text-amber-600 italic">
              *Quantity sesuai resep dokter, tidak dapat diubah
            </div>
          </div>
        </div>

        {index < array.length - 1 && <hr className="my-2" />}
      </div>
    );
  };

  useEffect(() => {
    if (cartDetailData && cartDetailData.length > 0) {
      const formattedData = cartDetailData.map((data) => ({
        ...data,
        image: formatImageToArray(data.image),
        isLoading: false,
      }));
      setDataBuffer(formattedData);
    } else {
      setDataBuffer([]);
    }
  }, [cartDetailData]);

  const groupedCartData = useMemo(() => {
    return groupCartDataByDoctor(dataBuffer);
  }, [dataBuffer, expandedItems]);

  const getExpandKey = (doctorGroup) =>
    doctorGroup.consultationId || `doctor-${doctorGroup.doctorId}`;

  const CheckoutSummary = () => {
    const selectedItems = dataBuffer.filter((item) => item.check_state);

    const handleCheckout = () => {
      if (selectedItems.length > 0) {
        if (!isLogin) {
          navigate("/profile");
        } else if (dataList === null) {
          showNotification("Silahkan tambahkan alamat terlebih dahulu", "info");
          navigate("/location-address/my-address/new");
        } else {
          navigate("/purchase/checkout", {
            state: { type: 1 },
          });
        }
      }
    };

    return (
      <>
        <div className="flex items-center justify-between font-semibold mb-3 md:mb-4">
          <h2 className="text-gray-800 md:text-slate-800">Total Harga</h2>
          <p className="text-error text-lg md:text-xl tabular-nums">
            Rp
            {isCartLoading ? "-" : formatRupiah(countCartData?.total || 0)}
          </p>
        </div>

        <button
          onClick={handleCheckout}
          disabled={
            dataBuffer.filter((item) => item.check_state).length === 0 ||
            isAddToCartLoading
          }
          className="h-[40px] md:h-12 text-base border-2 border-secondary flex items-center justify-center bg-secondary text-white w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-secondaryHover md:hover:scale-[1.01] transform duration-200 hover:scale-100 disabled:bg-slate-300 disabled:border-slate-300 disabled:cursor-default md:shadow-md"
        >
          {isAddToCartLoading ? (
            <span className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Memproses...
            </span>
          ) : (
            `Beli (${countCartData?.count || 0})`
          )}
        </button>
      </>
    );
  };

  return (
    <div className="w-full md:mt-4">
      <div className="flex flex-row justify-center">
        <div className="w-full max-w-7xl mx-auto flex flex-col md:flex-row md:items-start md:gap-8 lg:gap-10 text-base">
          <div className="min-w-0 w-full">
            <div className="hidden md:block mb-6 my-4">
              <nav className="text-base text-slate-600 mb-3 flex flex-wrap items-center gap-1 font-medium">
                <button
                  type="button"
                  onClick={() => navigate("/")}
                  className="hover:text-slate-900 hover:underline transition-colors"
                >
                  Beranda
                </button>
                <span className="text-slate-400">
                  /
                </span>
                <span className="text-primary">Keranjang</span>
              </nav>
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <h1 className="text-2xl lg:text-3xl font-semibold text-slate-800 tracking-tight">
                    Keranjang Belanja
                  </h1>
                  <p className="text-slate-500 mt-1 text-sm lg:text-base">
                    Review item dan lanjut checkout saat sudah siap.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="shrink-0 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white/80 px-4 py-2 text-md font-medium text-slate-700 shadow-sm hover:bg-secondaryActive hover:border-secondary/30 hover:text-secondary transition-colors"
                >
                  <IoArrowBack className="text-slate-500" size={18} />
                  Kembali
                </button>
              </div>
            </div>

            <div className="w-full flex gap-4 mb-12">
              <div className="w-full">
                {isCartLoading ? (
                  <SkeletonLoader />
                ) : (
                  <>
                    {Object.values(groupedCartData.withDoctor).map(
                      (doctorGroup) => {
                        const expandKey = getExpandKey(doctorGroup);
                        const isExpanded = expandedItems[expandKey] || false;

                        return (
                          <div
                            key={expandKey}
                            className="bg-white rounded-xl mb-3 md:mb-4 md:rounded-3xl md:border md:border-slate-200/90 md:shadow-[0_8px_24px_rgba(15,23,42,0.08)] md:bg-white/95 md:backdrop-blur-sm overflow-hidden"
                          >
                            <div className="p-3 md:p-4 lg:p-5">
                              <div className="flex items-start">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 mt-2 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary focus:ring-2 shrink-0"
                                  checked={doctorGroup.items.every(
                                    (item) => item.check_state,
                                  )}
                                  onChange={(e) =>
                                    handleDoctorCheckAllChange(doctorGroup, e)
                                  }
                                />

                                <button
                                  onClick={() => toggleExpand(expandKey)}
                                  className={`w-full flex justify-between items-start py-1 text-slate-80 ml-2 ${
                                    isExpanded
                                      ? "border-b pb-2 border-slate-200"
                                      : ""
                                  }`}
                                >
                                  <div className="flex flex-col items-start flex-1 min-w-0">
                                    <p className="text-md text-left font-medium truncate w-full max-w-[calc(100%-24px)] mt-1">
                                      Resep {doctorGroup.doctorName}
                                    </p>
                                    {doctorGroup.consultationId && (
                                      <p className="text-sm text-primary truncate">
                                        {doctorGroup.consultationId}
                                      </p>
                                    )}
                                    <div className="text-gray-500 flex items-center gap-1 text-sm mt-0.5">
                                      <IoLockClosedOutline />
                                      <p>Dosis sesuai resep dokter</p>
                                    </div>
                                  </div>
                                  <div className="text-gray-500 shrink-0 ml-2 mt-1">
                                    {isExpanded ? (
                                      <MdExpandLess size={16} />
                                    ) : (
                                      <MdExpandMore size={16} />
                                    )}
                                  </div>
                                </button>
                              </div>

                              {isExpanded && (
                                <div className="transition-all duration-300 ease-in-out bg-white pt-2">
                                  {doctorGroup.items.map((item, index) =>
                                    renderDoctorPrescriptionItem(
                                      item,
                                      index,
                                      doctorGroup.items,
                                      doctorGroup,
                                    ),
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      },
                    )}

                    {groupedCartData.withoutDoctor.length > 0 && (
                      <div className="bg-white rounded-xl p-3 md:p-5 md:rounded-3xl md:border md:border-slate-200/90 md:shadow-[0_8px_24px_rgba(15,23,42,0.08)] md:bg-white/95 md:backdrop-blur-sm">
                        {groupedCartData.withoutDoctor.map((item, index) =>
                          renderRegularProductItem(
                            item,
                            index,
                            groupedCartData.withoutDoctor,
                          ),
                        )}
                      </div>
                    )}
                  </>
                )}

                {dataBuffer.length === 0 && (
                  <div className="min-h-[calc(100vh-200px)] md:min-h-[420px] w-full flex items-center justify-center md:rounded-3xl md:border md:border-dashed md:border-slate-300/80 md:bg-white/70 md:px-8 md:py-12 md:shadow-[0_8px_24px_rgba(15,23,42,0.06)]">
                    <div className="flex flex-col items-center justify-center text-slate-700 px-4">
                      <img
                        src={NotFound}
                        alt="Not Found"
                        className="w-1/2 mb-3"
                      />
                      <h1 className="font-semibold text-lg text-center">
                        Belum ada produk di keranjang
                      </h1>
                      <p className="text-center text-md mt-2">
                        Silahkan tambahkan produk ke keranjang terlebih dahulu.
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <aside className="hidden md:block md:w-[380px] shrink-0">
                <div className="sticky top-[82px] bg-white rounded-3xl p-6 shadow-md">
                  <h1 className="font-semibold text-lg uppercase text-secondary mb-1">
                    Ringkasan
                  </h1>
                  <p className="mb-4 text-md text-slate-500">
                    Total untuk item yang akan dibeli.
                  </p>
                  <CheckoutSummary />
                </div>
              </aside>
              <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t p-4 z-50 shadow-xl">
                <div className="text-base flex flex-col bg-white px-3 py-4 rounded-t-3xl shadow-2xl max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0 max-md:z-[99] max-md:max-h-[70vh] md:z-auto md:max-h-none md:rounded-3xl md:border md:border-slate-200/90 md:shadow-[0_14px_36px_rgba(15,23,42,0.12)] md:px-6 md:py-6 md:sticky md:top-24 md:bg-white/95 md:backdrop-blur-sm">
                  <CheckoutSummary />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SwipeConfirm
        open={openModalDelete}
        onClose={() => setOpenModalDelete(false)}
        title={"Hapus Dari Keranjang?"}
        subTitle={"Hapus"}
        desc={"Produk akan dihapus dari keranjang."}
        handleConfirm={() => {
          if (selectedItem !== null) {
            handleDeleteClick(selectedItem);
          }
          setOpenModalDelete(false);
        }}
      />
    </div>
  );
};

export default CartDetailNew;
